import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ImageUp, RotateCcw } from "lucide-react";
import { ApiError, apiFetch } from "@/lib/api";
import {
  regenerateAdminThumbnail,
  retryAdminMatchSlot,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import type { Match } from "@/lib/types";
import {
  SLOT_LABELS,
  slotsForMatch,
  type MatchSlot,
} from "@/features/matches/matchSlots";
import { useTranscodeProgress } from "../hooks/useAdminData";
import { AdminIconButton } from "@/components/console/AdminIconButton";

/**
 * M8a B3 + B4 — per-match slot diagnostics panel.
 *
 * Renders inside the Match recordings dialog. One card per slot in the
 * match's configured recording layout, plus the production thumbnail and
 * audit controls. Each slot exposes
 * the operator recovery action set:
 *   - Verify (read-only, opens the asset check modal)
 *   - Regen HLS (rebuilds the HLS variant ladder from existing MP4)
 *   - Re-transcode (re-encode from source upload)
 *   - Retry  (error → transcoding)
 *   - Force Re-transcode (ready → transcoding override, with confirm)
 *   - Logs   (opens the errors list for the slot)
 *
 * Force / Re-transcode + Regen-HLS go through `<ConfirmDialog>`
 * because they're destructive to a working slot. Retry on an error
 * slot is a benign action (no confirm).
 *
 * Live transcode progress reads from `useTranscodeProgress` (5 s
 * poll, gated on "any slot transcoding" at the page level).
 */
export function MatchSlotPanel({
  match,
  onUpload,
}: {
  match: Match;
  onUpload?: (slot: MatchSlot) => void;
}) {
  const slots = slotsForMatch(match);
  const progressQuery = useTranscodeProgress({ enabled: true });
  const progress = progressQuery.data ?? {};

  return (
    <div className="admin-match-recordings-content">
      <div className="admin-match-recordings-grid">
        {slots.map((slot) => (
          <SlotCard
            key={slot}
            match={match}
            slot={slot}
            status={match.video_status?.[slot] ?? "none"}
            progress={progress[`${match.id}/${slot}`]}
            onUpload={onUpload}
          />
        ))}
      </div>

      <div className="admin-match-recording-operations">
        <ThumbnailCard match={match} />
      </div>
      <div className="admin-match-recording-audit text-[0.6rem] font-mono text-ink-muted">
        <span>
          Match ID: <span className="text-ink">{match.id}</span> ·
          updated {match.updated_at || "—"}
        </span>
      </div>
    </div>
  );
}

function SlotCard({
  match,
  slot,
  status,
  progress,
  onUpload,
}: {
  match: Match;
  slot: MatchSlot;
  status: string;
  progress: { active: boolean; pct?: number; stage?: string; elapsed_s?: number } | undefined;
  onUpload?: (slot: MatchSlot) => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);

  // Club consoles retry ERROR slots only — never `force` (an operator tool;
  // the backend 403s it on club hosts). Deep recovery (regen HLS, verify,
  // transcode logs, force re-transcode) lives on the super-admin Video
  // pipeline (admin-console-split PR 5).
  const retry = useMutation({
    mutationFn: () => retryAdminMatchSlot(match.id, slot, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") });
      queryClient.invalidateQueries({ queryKey: unscopedKey("club-overview") });
      toast.success("Slot transcode retry queued");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't retry the slot transcode", {
        description: formatErr(err),
      });
    },
  });

  const isReady = status === "ready";
  const isError = status === "error";
  const isTranscoding = status === "transcoding";

  return (
    <div
      data-testid="admin-slot-card"
      data-slot={slot}
      data-slot-status={status}
      className="admin-match-recording-card"
    >
      <header>
        <h3>{slot === "extra_time" ? "Extra time (overtime)" : SLOT_LABELS[slot]}</h3>
        <span
          data-tone={
            isReady ? "good" : isTranscoding ? "busy" : isError ? "bad" : "idle"
          }
          className={`admin-match-recording-status ${
            isReady
              ? "border-good/40 text-good"
              : isTranscoding
                ? "border-accent text-accent"
                : isError
                  ? "border-destructive/40 text-destructive"
                  : "border-rule text-ink-muted"
          }`}
        >
          {isReady
            ? "Available"
            : isTranscoding
              ? "Processing"
              : isError
                ? "Error"
                : slot === "extra_time" || slot === "penalty_shootout"
                  ? "Optional"
                  : "Unavailable"}
        </span>
      </header>

      <p className="admin-match-recording-copy">
        {isReady
          ? "Open or replace this recording."
          : isTranscoding
            ? "This recording is being prepared."
            : isError
              ? "Processing failed. Retry it or replace the recording."
              : slot === "extra_time" || slot === "penalty_shootout"
                ? "Upload if this part was played and recorded."
                : "Upload a recording for this slot."}
      </p>

      {isTranscoding && progress ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          {progress.stage ?? "encoding"} · {(progress.pct ?? 0).toFixed(0)}%
          {progress.elapsed_s ? ` · ${Math.round(progress.elapsed_s)}s` : ""}
        </p>
      ) : null}

      {error ? (
        <p
          data-testid="admin-slot-error"
          className="text-destructive font-mono text-[0.6rem]"
        >
          {error}
        </p>
      ) : null}

      <div className="admin-match-recording-actions">
        {isReady ? (
          <a href={`/match/${match.slug}/${slot.replaceAll("_", "-")}`}>
            Watch
          </a>
        ) : null}
        {onUpload ? (
          <button type="button" onClick={() => onUpload(slot)}>
            {isReady ? "Replace recording" : "Upload recording"}
          </button>
        ) : null}
        {isError ? (
          <SlotActionButton
            testid="admin-slot-retry"
            label="Retry"
            icon={RotateCcw}
            onClick={() => {
              setError(null);
              retry.mutate();
            }}
            disabled={retry.isPending}
          />
        ) : null}
        {isError ? (
          <p className="self-center text-[0.6rem] text-ink-muted">
            Still failing after a retry? Contact Tktic support.
          </p>
        ) : null}
      </div>

    </div>
  );
}

function SlotActionButton({
  icon,
  label,
  onClick,
  disabled,
  danger,
  testid,
}: {
  icon: Parameters<typeof AdminIconButton>[0]["icon"];
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  testid: string;
}) {
  return (
    <AdminIconButton
      icon={icon}
      label={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      tone={danger ? "destructive" : "neutral"}
    />
  );
}

function ThumbnailCard({ match }: { match: Match }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const regen = useMutation({
    mutationFn: () => regenerateAdminThumbnail(match.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") });
      toast.success("Thumbnail regeneration queued");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't regenerate the thumbnail", {
        description: formatErr(err),
      });
    },
  });

  return (
    <div
      data-testid="admin-thumbnail-card"
      className="flex flex-col gap-2 rounded border border-rule bg-surface p-2"
    >
      <header className="flex items-center justify-between">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Thumbnail
        </span>
        <span
          className={`rounded border px-1.5 py-0.5 text-[0.6rem] font-mono ${
            match.has_thumbnail
              ? "border-good/40 text-good"
              : "border-rule text-ink-muted"
          }`}
        >
          {match.has_thumbnail ? "present" : "missing"}
        </span>
      </header>
      {error ? (
        <p className="text-destructive font-mono text-[0.6rem]">{error}</p>
      ) : null}
      <AdminIconButton
        icon={ImageUp}
        label={regen.isPending ? "Regenerating thumbnail" : "Regenerate thumbnail"}
        onClick={() => {
          setError(null);
          regen.mutate();
        }}
        disabled={regen.isPending}
        data-testid="admin-thumbnail-regenerate"
      />
    </div>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

// `apiFetch` re-imported so the file is self-contained for the
// fetch wrappers used in mutations.
void apiFetch;
